// Optimized Saga File
import { all, takeEvery, put, call } from "redux-saga/effects";
import { toast } from "react-toastify";
import {
  Toast,
  getTenantId,
  isSuccess,
  showError,
  toastMessage,
} from "../../utils/helper";
import { TOAST_TYPES } from "../../utils/constant";
import * as TYPES from "../action/types";
import * as ACTIONS from "../action";
import INTERVIEW from "../../utils/api/interview";
import POSITION from "../../utils/api/candidate";
import SKILL from "../../utils/api/skill";
import USERS from "../../utils/api/users";
import { STATUSES } from "../../component/Questions/utils/constants";

function* handleAPI({
  apiFn,
  args = [],
  successAction,
  failureAction,
  onSuccess,
  onError,
}) {
  try {
    const res = yield call(apiFn, ...args);
    if (isSuccess(res.status)) {
      if (successAction) yield put(successAction(res.data?.result));
      if (onSuccess) yield call(onSuccess, res.data?.result);
    } else {
      if (failureAction) yield put(failureAction());
    }
  } catch (e) {
    if (failureAction) yield put(failureAction());
    if (onError !== false) yield showError(e);
  }
}

function* getPositionRequest(action) {
  try {
    const isSearch = action.payload?.search
      ? `&search=${encodeURIComponent(action.payload?.search)}`
      : "";
    const isPageAndPageSize =
      action.payload?.page && action.payload?.pageSize
        ? `?page=${action.payload.page}&pageSize=${action.payload?.pageSize}`
        : "";
    const isSort = action.payload.filter
      ? action.payload.filter.sort
        ? `&sort=${action.payload.filter.sort}`
        : action.payload.sort
          ? `&sort=${action.payload.sort}`
          : ""
      : action.payload.sort
        ? `&sort=${action.payload.sort}`
        : "";
    const isFilter = action.payload.filter
      ? (action.payload.filter.from
          ? action.payload.filter.to
            ? `&from=${action.payload.filter.from}T00:00:00&to=${action.payload.filter.to}T23:59:59`
            : `&from=${action.payload.filter.from}T00:00:00&to=3000-12-31T23:59:59`
          : action.payload.filter.to
            ? `&from=2000-01-01T00:00:00&to=${action.payload.filter.to}T23:59:59`
            : "") +
        (action.payload.filter.status
          ? `&status=${action.payload.filter.status}`
          : "") +
        (action.payload.filter.createdBy
          ? `&createdBy=${action.payload.filter.createdBy}`
          : "")
      : "";
    const res = yield POSITION.get(
      `incscreen/positions/getPositions${isPageAndPageSize}${isSearch}${isFilter}${isSort}`,
    );
    if (res.status === 201 || res.status === 200) {
      if (action?.payload?.page === "all") {
        yield put(ACTIONS.getPositionFailure());
        yield call(action.payload.callback, res.data.result);
      } else {
        yield put(ACTIONS.getPositionSuccess(res.data.result));
      }
    }
  } catch (e) {
    yield put(ACTIONS.getPositionFailure());
    yield e?.response?.data?.message &&
      e?.response?.data?.message !==
        "Your session has been expired, please login again!" &&
      toast.error(<Toast msg={e?.response?.data?.message} />, {
        autoClose: 5000,
        toastId: e?.response?.data?.message,
      });
  }
}

function* updatePositionStatusRequest(action) {
  yield call(handleAPI, {
    apiFn: POSITION.put,
    args: [
      `incscreen/positions/statusUpdatePosition/${action.payload.positionId}/${action.payload.status}`,
    ],
    successAction: ACTIONS.updatePositionStatusSuccess,
    failureAction: ACTIONS.updatePositionStatusFailure,
    onSuccess: function* (result) {
      if (result?.error || result?.message) {
        toastMessage(TOAST_TYPES.ERROR, result.error || result.message);
      } else {
        const message =
          result.status === STATUSES.CLOSED
            ? "Position Closed Successfully"
            : "Position Reopen Successfully";
        yield call(action.payload.callback, result);
        toastMessage(TOAST_TYPES.SUCCESS, message);
      }
    },
  });
}

function* postPositionRequest(action) {
  const tenantId = getTenantId();
  const headers = {
    headers: {
      "content-type": "multipart/form-data",
      ...(tenantId && { "x-tenant-id": tenantId }),
    },
  };
  const { title, domain, minExp, maxExp, noOfPositions } = action.payload.form;
  const { jobDescription, replicateId, positionId } = action.payload.form;
  const followupConfig = action.payload.followupConfig;
  const formData = new FormData();
  formData.append("title", title);
  formData.append("domain", domain);
  formData.append(
    "expRange",
    JSON.stringify({ minYears: minExp, maxYears: maxExp }),
  );
  formData.append("skills", JSON.stringify(action?.payload?.skills));
  formData.append(
    "customSkills",
    JSON.stringify(action?.payload?.customSkills),
  );
  if (noOfPositions !== "") formData.append("noOfPositions", noOfPositions);
  else formData.append("noOfPositions", 1);
  if (positionId) formData.append("positionId", positionId);
  if (replicateId) formData.append("replicateId", replicateId);
  if (followupConfig)
    formData.append("followupConfig", JSON.stringify(followupConfig));
  if (jobDescription && action?.payload?.fileChange)
    formData.append("jobDescription", jobDescription);
  yield call(handleAPI, {
    apiFn: POSITION.post,
    args: [`incscreen/positions/createAndUpdatePosition`, formData, headers],
    successAction: ACTIONS.createOrUpdatePositionSuccess,
    failureAction: ACTIONS.createOrUpdatePositionFailure,
    onSuccess: function* (result) {
      if (result?.error || result?.message) {
        toastMessage(TOAST_TYPES.ERROR, result.error || result.message);
      } else {
        let message;
        if (action.payload.form?.positionId)
          message = "Position updated successfully.";
        else message = "Position created successfully.";
        yield call(action.payload.callback, result);
        toastMessage(TOAST_TYPES.SUCCESS, message);
      }
    },
  });
}

function* deletePositionRequest(action) {
  yield call(handleAPI, {
    apiFn: POSITION.delete,
    args: [`incscreen/positions/deletePosition/${action.payload.positionId}`],
    successAction: ACTIONS.deletePositionSuccess,
    failureAction: ACTIONS.deletePositionFailure,
    onSuccess: function* (result) {
      if (result?.error || result?.message) {
        toastMessage(TOAST_TYPES.ERROR, result.error || result.message);
      } else {
        yield call(action.payload.callback, result);
        toastMessage(TOAST_TYPES.SUCCESS, "Position Deleted Successfully");
      }
    },
  });
}

function* fetchSkillsRequest(action) {
  yield call(handleAPI, {
    apiFn: SKILL.post,
    args: ["skills/fetch-skills", action.payload.data],
    successAction: ACTIONS.fetchSkillsSuccess,
    failureAction: ACTIONS.fetchSkillsFailure,
    onSuccess: function* (result) {
      if (result?.error || result?.message) {
        toastMessage(TOAST_TYPES.ERROR, result.error || result.message);
      } else {
        yield call(action.payload.callback, result);
        toastMessage(TOAST_TYPES.SUCCESS, "Skills fetch successfully.");
      }
    },
  });
}

function* fetchSkillsTitleRequest(action) {
  yield call(handleAPI, {
    apiFn: POSITION.post,
    args: ["incscreen/positions/gptSkillset", action.payload.data],
    successAction: ACTIONS.fetchSkillsTitleSuccess,
    failureAction: ACTIONS.fetchSkillsTitleFailure,
    onSuccess: function* (result) {
      if (result?.error && result?.message) {
        toastMessage(TOAST_TYPES.ERROR, result.message);
      } else {
        yield call(action.payload.callback, result);
        toastMessage(TOAST_TYPES.SUCCESS, "Skills fetch successfully.");
      }
    },
  });
}

function* fetchSkillsJDRequest(action) {
  const formData = new FormData();
  formData.append("jd", action.payload.jobDescription);
  const tenantId = getTenantId();
  yield call(handleAPI, {
    apiFn: POSITION.post,
    args: [
      "incscreen/positions/gptjdSkillset",
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
          ...(tenantId && { "x-tenant-id": tenantId }),
        },
      },
    ],
    successAction: ACTIONS.fetchSkillsJDSuccess,
    failureAction: ACTIONS.fetchSkillsJDFailure,
    onSuccess: function* (result) {
      if (result?.error && result?.message) {
        toastMessage(TOAST_TYPES.ERROR, result.message);
      } else {
        yield call(action.payload.callback, result);
        toastMessage(TOAST_TYPES.SUCCESS, "Skills fetch successfully.");
      }
    },
  });
}

function* getDomainRequest() {
  yield call(handleAPI, {
    apiFn: SKILL.get,
    args: ["domains"],
    successAction: ACTIONS.getDomainSuccess,
    failureAction: ACTIONS.getDomainFailure,
  });
}

function* getWhiteLabelRequest() {
  yield call(handleAPI, {
    apiFn: USERS.get,
    args: ["employers/employerLogo"],
    successAction: ACTIONS.getWhiteLabelSuccess,
    failureAction: ACTIONS.getWhiteLabelFailure,
  });
}

function* getPositionListRequest() {
  yield call(handleAPI, {
    apiFn: INTERVIEW.get,
    args: ["positionlists"],
    successAction: ACTIONS.getPositionListSuccess,
    failureAction: ACTIONS.getPositionListFailure,
  });
}

function* getAllUsersRequest() {
  yield call(handleAPI, {
    apiFn: USERS.get,
    args: ["users/dashboard/dropdown"],
    successAction: ACTIONS.getAllUsersSuccess,
    failureAction: ACTIONS.getAllUsersFailure,
  });
}

function* getMandatorySkillRequest() {
  yield call(handleAPI, {
    apiFn: SKILL.get,
    args: ["skills?type=mandatory"],
    successAction: ACTIONS.getMandatorySkillSuccess,
    failureAction: ACTIONS.getMandatorySkillFailure,
  });
}

function* getOptionalSkillRequest() {
  yield call(handleAPI, {
    apiFn: SKILL.get,
    args: ["skills?type=optional"],
    successAction: ACTIONS.getOptionalSkillSuccess,
    failureAction: ACTIONS.getOptionalSkillFailure,
  });
}

function* getPositionByIdRequest({ payload }) {
  try {
    const res = yield POSITION.get(
      `incscreen/positions/getPositions/${payload.positionId}`,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.getPositionByIdSuccess(res.data.result));
      if (payload.callback) {
        yield call(payload.callback, res.data.result);
      }
    }
  } catch (e) {
    yield put(ACTIONS.getPositionByIdFailure());
    if (payload.failure) {
      yield call(payload.failure);
    }
    yield call(showError, e);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(TYPES.GET_POSITION, getPositionRequest),
    takeEvery(TYPES.POST_POSITION, postPositionRequest),
    takeEvery(TYPES.DELETE_POSITION, deletePositionRequest),
    takeEvery(TYPES.GET_DOMAIN, getDomainRequest),
    takeEvery(TYPES.GET_WHITE_LABEL, getWhiteLabelRequest),
    takeEvery(TYPES.GET_POSITION_LIST, getPositionListRequest),
    takeEvery(TYPES.GET_ALL_USERS, getAllUsersRequest),
    takeEvery(TYPES.UPDATE_POSITION_STATUS, updatePositionStatusRequest),
    takeEvery(TYPES.GET_MANDATORY_SKILL, getMandatorySkillRequest),
    takeEvery(TYPES.GET_OPTIONAL_SKILL, getOptionalSkillRequest),
    takeEvery(TYPES.FETCH_SKILLS_TITLE, fetchSkillsTitleRequest),
    takeEvery(TYPES.FETCH_SKILLS, fetchSkillsRequest),
    takeEvery(TYPES.FETCH_SKILLS_JD, fetchSkillsJDRequest),
    takeEvery(TYPES.GET_POSITION_BY_ID, getPositionByIdRequest),
  ]);
}
